// src/components/authorizations/TestConnectionSection.tsx
//
// Superadmin-only drawer section that probes a stored authorization
// against its upstream. Renders the structured response inline as a
// coloured Alert — no navigation, no mutations.

import { useState } from "react";
import {
  Alert,
  Button,
  Code,
  Group,
  List,
  Stack,
  Text,
} from "@mantine/core";
import { IconCheck, IconPlugConnected, IconX } from "@tabler/icons-react";

import { testAuthorizationConnection } from "@/api/authorizations";
import type { TestConnectionResult } from "@/types/authorizations";
import { formatExpiry, ScopeList } from "./authorizationFormatters";

type Props = {
  authorizationId: string;
  isSuperadmin: boolean;
  authType: string; // normalized — "FACEBOOK", "BIGQUERY", ...
};

const STAGE_LABELS: Record<string, string> = {
  debug_token: "Token introspection",
  account_info: "Ad account access",
  insights: "Insights reachability",
  list_accounts: "Ad account listing",
};

function scopesDiffer(live: string[], stored: string[]): boolean {
  if (stored.length === 0) return false;
  const liveSet = new Set(live);
  const storedSet = new Set(stored);
  if (liveSet.size !== storedSet.size) return true;
  for (const s of liveSet) {
    if (!storedSet.has(s)) return true;
  }
  return false;
}

export function TestConnectionSection({
  authorizationId,
  isSuperadmin,
  authType,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TestConnectionResult | null>(null);

  if (!isSuperadmin) return null;

  async function runTest() {
    setLoading(true);
    try {
      const r = await testAuthorizationConnection(authorizationId);
      setResult(r);
    } catch (e) {
      // Fetch itself failed (network offline, abort, etc.) — surface as
      // a synthetic error result so the renderer stays on a single path.
      setResult({
        authorization_id: authorizationId,
        type: authType,
        status: "error",
        detail: null,
        error: {
          stage: "list_accounts",
          code: null,
          message: e instanceof Error ? e.message : "Network error",
          missing_scopes: [],
        },
        http_status: 0,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Stack gap="xs">
      <Text fw={600}>Test connection</Text>
      <Text size="sm" c="dimmed">
        Probe the stored credentials against the upstream API. Read-only.
      </Text>

      <Group justify="flex-end" align="center" gap="sm">
        <Text size="xs" c="dimmed">
          Superadmin
        </Text>
        <Button
          variant="light"
          color="grape"
          leftSection={<IconPlugConnected size={16} />}
          onClick={runTest}
          loading={loading}
          disabled={loading}
        >
          Test connection
        </Button>
      </Group>

      {result ? <ResultView result={result} authType={authType} /> : null}
    </Stack>
  );
}

function ResultView({
  result,
  authType,
}: {
  result: TestConnectionResult;
  authType: string;
}) {
  if (result.http_status === 501) {
    return (
      <Alert color="gray" title={`Not supported for ${authType}`}>
        Test-connection is currently only implemented for FACEBOOK
        authorizations.
      </Alert>
    );
  }

  if (result.status === "error" || !result.detail) {
    return <ErrorView result={result} />;
  }

  const detail = result.detail;

  // Access-token path, token valid but insights endpoint failed
  if (detail.token_is_valid && detail.insights_ok === false) {
    return (
      <Alert color="yellow" title="Partial success">
        <Stack gap="xs">
          <Text size="sm">
            Token works and ad account is accessible, but the insights
            endpoint returned an error. Reporting may fail.
          </Text>
          <SuccessDetails detail={detail} />
        </Stack>
      </Alert>
    );
  }

  return (
    <Alert
      color="green"
      title="Connection OK"
      icon={<IconCheck size={16} />}
    >
      <SuccessDetails detail={detail} />
    </Alert>
  );
}

function SuccessDetails({
  detail,
}: {
  detail: NonNullable<TestConnectionResult["detail"]>;
}) {
  const tokenExpiry = formatExpiry(detail.token_expires_at);
  const dataAccessExpiry = formatExpiry(detail.data_access_expires_at);
  const showDrift = scopesDiffer(detail.token_scopes, detail.stored_token_scopes);

  return (
    <Stack gap="xs" mt={4}>
      <Group gap="xs">
        <IconCheck size={14} color="var(--mantine-color-green-7)" />
        <Text size="sm">Token is valid</Text>
      </Group>

      {tokenExpiry ? (
        <Group gap="xs" justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed">
            Token expires
          </Text>
          <Text size="sm" fw={500} style={{ textAlign: "right" }}>
            {tokenExpiry}
          </Text>
        </Group>
      ) : null}

      {dataAccessExpiry ? (
        <Group gap="xs" justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed">
            Data access expires
          </Text>
          <Text size="sm" fw={500} style={{ textAlign: "right" }}>
            {dataAccessExpiry}
          </Text>
        </Group>
      ) : null}

      <ScopeList scopes={detail.token_scopes} label="Scopes" />

      {showDrift ? (
        <>
          <Text size="xs" c="dimmed">
            Stored scopes differ from live
          </Text>
          <ScopeList
            scopes={detail.stored_token_scopes}
            label="Stored"
          />
        </>
      ) : null}

      {detail.accessible_ad_accounts_count !== null ? (
        <Group gap="xs" justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed">
            Accessible ad accounts
          </Text>
          <Text size="sm" fw={500}>
            {detail.accessible_ad_accounts_count}
            {detail.accessible_ad_accounts_count === 0 ? (
              <Text component="span" size="xs" c="yellow.7" ml="xs">
                Token works but no ad accounts assigned
              </Text>
            ) : null}
          </Text>
        </Group>
      ) : null}

      {detail.ad_account_id ? (
        <>
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <Text size="sm" c="dimmed">
              Ad account
            </Text>
            <Group gap="xs" wrap="nowrap">
              <Code>{detail.ad_account_id}</Code>
              {detail.account_name ? (
                <Text size="sm" fw={500}>
                  {detail.account_name}
                  {detail.currency || detail.timezone
                    ? ` (${[detail.currency, detail.timezone]
                        .filter(Boolean)
                        .join(", ")})`
                    : ""}
                </Text>
              ) : null}
            </Group>
          </Group>
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <Text size="sm" c="dimmed">
              Insights reachable
            </Text>
            {detail.insights_ok ? (
              <IconCheck size={16} color="var(--mantine-color-green-7)" />
            ) : (
              <IconX size={16} color="var(--mantine-color-red-7)" />
            )}
          </Group>
        </>
      ) : null}
    </Stack>
  );
}

function ErrorView({ result }: { result: TestConnectionResult }) {
  const stage = result.error?.stage ?? "list_accounts";
  const stageLabel = STAGE_LABELS[stage] ?? stage;
  const message = result.error?.message ?? "Something went wrong.";
  const missingScopes = result.error?.missing_scopes ?? [];
  const liveScopes = result.detail?.token_scopes ?? [];

  return (
    <Alert color="red" title={`Connection failed — ${stageLabel}`}>
      <Stack gap="xs">
        <Text size="sm">{message}</Text>

        {missingScopes.length > 0 ? (
          <>
            <Text size="sm" fw={500}>
              Missing scopes
            </Text>
            <List size="sm" spacing={2}>
              {missingScopes.map((s) => (
                <List.Item key={s}>{s}</List.Item>
              ))}
            </List>
          </>
        ) : null}

        {stage === "debug_token" && liveScopes.length > 0 ? (
          <ScopeList scopes={liveScopes} label="Current scopes" />
        ) : null}
      </Stack>
    </Alert>
  );
}
